import { Toaster } from "@/components/ui/sonner"
import { ZeroInit } from "@/contexts/zero"
import { AuthProvider } from "@/providers/auth"
import { ThemeProvider } from "@/providers/theme"
import "@xyflow/react/dist/style.css"
import {
	MutationCache,
	QueryCache,
	QueryClient,
	QueryClientProvider,
} from "@tanstack/react-query"
import { StrictMode, useMemo, type ReactNode } from "react"
import { BrowserRouter, HashRouter } from "react-router"
import { toast } from "sonner"
import { OriginShell } from "./OriginShell"
import { ElectronOnboarding } from "./ElectronOnboarding"
import { PlatformProvider } from "../platform/context"
import type { AppPlatform } from "../platform/types"

const getErrorMessage = (error: unknown) => {
	if (error instanceof Error && error.message) {
		return error.message
	}

	if (typeof error === "string" && error.length > 0) {
		return error
	}

	return "Something went wrong"
}

const createQueryClient = () =>
	new QueryClient({
		mutationCache: new MutationCache({
			onError: (error) => {
				const message = getErrorMessage(error)
				toast.error(message)
			},
		}),
		queryCache: new QueryCache({
			onError: (error) => {
				const message = getErrorMessage(error)
				toast.error(message)
			},
		}),
	})

export const AppProviders = ({
	children,
	platform,
}: {
	children: ReactNode
	platform: AppPlatform
}) => {
	const queryClient = useMemo(() => createQueryClient(), [])
	const Router = platform.kind === "electron" ? HashRouter : BrowserRouter
	const content =
		platform.kind === "electron" ? (
			<ElectronOnboarding>
				<OriginShell>{children}</OriginShell>
			</ElectronOnboarding>
		) : (
			children
		)

	return (
		<StrictMode>
			<PlatformProvider platform={platform}>
				<QueryClientProvider client={queryClient}>
					<ThemeProvider
						attribute="class"
						defaultTheme="system"
						enableSystem
						storageKey="oblique-theme"
					>
						<Toaster richColors closeButton />
						<Router>
							<AuthProvider>
								<ZeroInit>{content}</ZeroInit>
							</AuthProvider>
						</Router>
					</ThemeProvider>
				</QueryClientProvider>
			</PlatformProvider>
		</StrictMode>
	)
}
